/**
 * services domain (doc 01 A1.1 / A2.x). The vertical slice uses the read paths
 * that answer "are today's services actually happening?" — scheduled (planned)
 * and events (delivered / in-progress). Types mirror the API's service views.
 *
 * NOTE (spec question): the current backend exposes no single "today's planned
 * vs delivered visits" summary; `/services/scheduled` and `/services/events`
 * accept only client/worker/session filters. The DeliveryBand composition
 * therefore derives today's counts from these lists client-side rather than
 * fabricating a summary. See report.
 *
 * THE PLAN WAS READ-ONLY FROM THIS SIDE, WHICH MADE EVERY PLANNED-VS-DELIVERED
 * FIGURE A ZERO NUMERATOR. `POST /services/scheduled` and
 * `PATCH /services/scheduled/:id` have existed on the server since
 * 1757040000000_scheduled-service-is-roster — with their own authority model,
 * their own soft-deleted-subject refusals, and a migration header arguing at
 * length about who may build a roster. Nothing on this side called either one.
 * So a visit could be DELIVERED but never PLANNED, and everything downstream
 * that compares the two measured delivery against a plan nobody could enter:
 * `apps/admin/src/features/delivery.ts` subtracts the delivered set from the
 * planned one to find missed visits, and a subtraction from an empty set finds
 * none, forever. A care-gap surface reading zero because the input is
 * unreachable is indistinguishable from one reading zero because care is
 * complete. See `createScheduled` / `updateScheduled` / `cancelScheduled`.
 *
 * AND THE DELIVERED HALF WAS READ-ONLY TOO, WHICH WAS WORSE. `events()` below
 * is the GET; there was no POST wrapper for `/services/events` in this file at
 * all, and `services/repository.ts:691` — the only production writer of
 * `service_event` besides a Ritten connector that has never been run against a
 * real tenant — was therefore unreachable from every shipped surface. Nothing
 * downstream of it could happen either: a `service_documentation_status` row
 * exists per service event, so the documentation queue stayed empty by
 * construction and the worker app's wrap-up flow had nothing to document. See
 * `createEvent`, at the foot of this file.
 */
import { get, patch, post } from './transport.js';
/**
 * The instant calendar day `day` (YYYY-MM-DD) begins in `timeZone` — the
 * frontend's `org_local_date()` inverse. Intl does the zone math, so a DST
 * day is right without walking milliseconds.
 */
export function startOfDayIn(day, timeZone) {
    const [y, m, d] = day.split('-').map(Number);
    const wall = Date.UTC(y, m - 1, d);
    const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
    });
    // The zone's offset at `t`: its wall clock read as UTC, minus `t`.
    const offset = (t) => {
        const p = Object.fromEntries(fmt.formatToParts(t).map((x) => [x.type, Number(x.value)]));
        return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - t;
    };
    // Twice: the offset at the first guess can sit across a DST change.
    const first = wall - offset(wall);
    return new Date(wall - offset(first));
}
/**
 * The read window for org-calendar days [from, toExclusive) — `toExclusive` is
 * the day AFTER the last, as a `PeriodLabel.to` is. The server's window is
 * closed at both ends, so the upper bound is the last instant before
 * `toExclusive` begins. `from: null` reads from the start of the records.
 */
export function dayWindow(from, toExclusive, timeZone) {
    return {
        ...(from ? { from: startOfDayIn(from, timeZone).toISOString() } : {}),
        to: new Date(startOfDayIn(toExclusive, timeZone).getTime() - 1).toISOString(),
    };
}
/** `GET /services/scheduled` → `{ scheduledServices, scoped }` */
export function scheduled(filter = {}, options) {
    return get('/services/scheduled', {
        ...options,
        query: { ...filter },
    });
}
/**
 * `GET /services/scheduled/:id` → the one planned visit, on the list's scope
 * (404 when it is missing OR outside the caller's reach — indistinguishable on
 * purpose). The drill-down a `/visits?occurrence=<id>` deep link opens.
 */
export async function scheduledById(id, options) {
    const body = await get(`/services/scheduled/${encodeURIComponent(id)}`, options);
    return body.scheduledService;
}
/**
 * `POST /services/scheduled/:id/session` — record that a time session was the
 * work done for a planned visit.
 *
 * The clock-in target link, and the ONLY way it is ever made: nothing infers it
 * from overlapping times, because a worker with three visits in an afternoon
 * produces sessions that overlap all of them and "which client was this hour
 * for" is not a question arithmetic may answer.
 *
 * Idempotent — re-sending the same pair returns `alreadyAssociated: true` and
 * changes nothing, which is what makes it safe to fire straight after a clock-in
 * that may itself have been retried. A session already tied to a DIFFERENT visit
 * is refused with 409 rather than moved.
 */
export function associateSessionWithVisit(scheduledServiceId, sessionId, options) {
    return post(`/services/scheduled/${encodeURIComponent(scheduledServiceId)}/session`, { sessionId }, options);
}
/** `GET /services/events` → `{ serviceEvents, scoped }` */
export function events(filter = {}, options) {
    return get('/services/events', {
        ...options,
        query: { ...filter },
    });
}
/** `GET /services/types` → `{ serviceTypes }` (needs `service:read`). */
export async function types(query = {}, options) {
    const body = await get('/services/types', {
        ...options,
        query: { ...query },
    });
    return body.serviceTypes;
}
/** `GET /services/targets` → `{ serviceTargets }` (needs `service:read`). */
export async function targets(filter = {}, options) {
    const body = await get('/services/targets', {
        ...options,
        query: { ...filter },
    });
    return body.serviceTargets;
}
/**
 * `POST /services/types` → `{ serviceType }` (needs `service:manage`). Defines
 * a new entry in the organization's service catalog.
 */
export async function createType(input, options) {
    const body = await post('/services/types', input, options);
    return body.serviceType;
}
/** `PATCH /services/types/:id` → `{ serviceType }` (needs `service:manage`). */
export async function updateType(id, input, options) {
    const body = await patch(`/services/types/${id}`, input, options);
    return body.serviceType;
}
// --- writing the PLANNED roster --------------------------------------------
/**
 * `POST /services/scheduled` → `{ scheduledService }`, 201 (needs
 * `schedule:write`).
 *
 * Books a planned visit. The key is the ROSTER key, not `service:manage`:
 * 1757040000000 settled that building the coverage plan is roster authority and
 * that whoever builds it must be able to staff people who do not report to them.
 */
export async function createScheduled(input, options) {
    const body = await post('/services/scheduled', input, options);
    return body.scheduledService;
}
/**
 * `PATCH /services/scheduled/:id` → `{ scheduledService }` (needs
 * `schedule:write`).
 *
 * Moves, re-times or re-staffs a planned visit. The server returns the row as it
 * now stands; this returns that row and never the request that produced it, so a
 * field the server declined to change cannot be mistaken for one it accepted.
 */
export async function updateScheduled(id, input, options) {
    const body = await patch(`/services/scheduled/${encodeURIComponent(id)}`, input, options);
    return body.scheduledService;
}
/**
 * Calls a planned visit off: `PATCH /services/scheduled/:id` with
 * `status: 'cancelled'` (needs `schedule:write`).
 *
 * A NAMED FUNCTION FOR ONE PATCH, because the alternative is every screen
 * open-coding the status string and one of them eventually reaching for a
 * DELETE that does not exist. Cancelling is not removal: the row stays on the
 * roster saying the visit was planned and then called off, which is what makes
 * it distinguishable from a visit nobody ever planned. `note` carries the reason
 * when the caller has one — the server takes it on the same patch, so recording
 * why does not cost a second round trip that could half-fail.
 */
export async function cancelScheduled(id, input = {}, options) {
    return updateScheduled(id, input.note === undefined ? { status: 'cancelled' } : { status: 'cancelled', note: input.note }, options);
}
/**
 * `POST /services/events` → `{ serviceEvent }`, 201.
 *
 * RECORDS THAT A VISIT HAPPENED — the row the rest of the operating loop is
 * made of, and until now the one thing no shipped surface could write.
 * `services/repository.ts:691` is the only production writer of `service_event`
 * besides the Ritten connector, which has never been run against a real tenant;
 * this package exported `events()` — the GET — and no POST at all. Downstream,
 * `service_documentation_status` rows are created per service event, so the
 * documentation queue was permanently empty and the worker app's wrap-up flow
 * had nothing to document. A queue that is empty because its input is
 * unreachable is indistinguishable from one that is empty because the work is
 * done.
 *
 * TWO KEYS OPEN THIS DOOR AND THEY ARE NOT INTERCHANGEABLE — `services/
 * routes.ts guardServiceEventSurface` admits `service:write` OR
 * `service:write_all`, and then the two are treated completely differently:
 *
 *   `service:write_all` — organization-wide, seeded to org_admin alone
 *     (1757010000000). `workerId` is taken AS SENT. This is the key that makes
 *     recording on somebody else's behalf possible at all.
 *   `service:write` — the ordinary key. `resolveDeliverySubject` resolves the
 *     caller's OWN worker row and refuses outright if there is none; a
 *     `workerId` naming anyone else is a 403, and an omitted one is silently
 *     filled in with the caller's own.
 *
 * So the SAME REQUEST is attributed differently depending on who sends it, and
 * a caller cannot tell which happened from the body it sent. THAT IS WHY THIS
 * RETURNS THE SERVER'S ROW AND NEVER THE INPUT: `serviceEvent.workerId` is who
 * the delivery is actually attributed to, and it is the only place that fact is
 * stated. 1757010000000 minted `service:write_all` precisely to stop an
 * ordinary holder fabricating a delivery against a colleague, and a client that
 * echoed its own request back would hide the difference the key exists to make.
 */
export async function createEvent(input, options) {
    const body = await post('/services/events', input, options);
    return body.serviceEvent;
}
/**
 * The one set of words both apps show for an occurrence's lifecycle. `Started
 * (empty)` is a session that began with nothing written yet — it counts as
 * delivered; `Not started` is a past appointment that never began.
 */
export const OCCURRENCE_STATE_LABELS = {
    booked: 'Booked',
    not_started: 'Not started',
    started_empty: 'Started (empty)',
    incomplete: 'Incomplete',
    complete: 'Complete',
    cancelled: 'Cancelled',
    no_show: 'No-show',
};
/**
 * OPERATIONALLY DELIVERED by the EHR's own word — the server's
 * `DELIVERED_OCCURRENCE_STATES` (occurrence-state.ts), pinned equal by
 * `services-occurrence-client-parity.test.ts`. A blank past occurrence is
 * delivered only on its encounter's evidence, which only `deliveredBucket`
 * knows: prefer the bucket when it is present.
 */
export const DELIVERED_OCCURRENCE_STATES = [
    'complete',
    'started_empty',
    'incomplete',
];
/**
 * The settle lag (owner ruling 2026-10-07): expected work counts as DUE once
 * its start is this many hours past. The server's `SETTLE_LAG_HOURS`
 * (apps/api/src/domains/schedule/scheduled-work.ts); change both together.
 */
export const SETTLE_LAG_HOURS = 48;
/** Phibian's own `status` values that settle a visit (scheduled_service_status_ck). */
const SETTLED_STATUSES = new Set(['cancelled', 'completed', 'no_show']);
/**
 * The canonical buckets still awaiting delivery — exactly the ones the server's
 * scheduled-work model maps to work_state 'expected' (scheduled-work.ts WORK),
 * the population the no-show board chases. Pinned there by
 * `services-occurrence-delivery-and-party-reach.test.ts`.
 */
const OPEN_BUCKETS = new Set([
    'booked',
    'not_started',
    'other_status',
]);
/**
 * Is this planned visit NO LONGER SOMETHING TO DO — delivered, cancelled, a
 * no-show, or no longer listed by the EHR? For action surfaces (now-next,
 * today's ring), not for tallies: count with `deliveredBucket`.
 *
 * Closed when EITHER side says so. Phibian's own `status` (session-close
 * settlement, a call-off, a discharge — they land before the EHR catches up),
 * or the EHR: the canonical `deliveredBucket` when present (it also closes a
 * blank past row its encounter proves), else `vendorAbsentSince`, else
 * `occurrenceState`. An imported visit's `status` stays 'scheduled' when the
 * EHR cancels or completes it, so `status` alone is never the answer for one.
 *
 * VENDOR-ABSENT IS CLOSED: it is not scheduled work (owner ruling) and the
 * server never treats it as expected, so an action surface must not chase it
 * either. A surface that shows it does so as "no longer in Ritten" —
 * `deliveredBucket === 'vendor_absent'` — never as a to-do. booked,
 * not_started and unclassified words stay OPEN: the safe direction of an
 * unsettled visit is "still yours".
 */
export function isVisitClosed(s) {
    if (SETTLED_STATUSES.has(s.status))
        return true;
    if (s.deliveredBucket)
        return !OPEN_BUCKETS.has(s.deliveredBucket);
    if (s.vendorAbsentSince)
        return true;
    if (s.occurrenceState) {
        return (DELIVERED_OCCURRENCE_STATES.includes(s.occurrenceState) ||
            s.occurrenceState === 'cancelled' ||
            s.occurrenceState === 'no_show');
    }
    return false;
}
/**
 * Is this planned visit SCHEDULED WORK (owner ruling 2026-10-07)? Every planned
 * visit except a cancelled one — by the EHR, whose cancellation outranks its no
 * longer listing the visit — and one the EHR no longer lists (shown on its own
 * as "no longer in the EHR", never counted). A no-show stays scheduled: it was
 * due and was not delivered.
 *
 * A TALLY, so it reads exactly what the server's tallies read (scheduled-work.ts
 * `WORK`): the canonical `deliveredBucket` alone when present — Phibian's own
 * `status` is not consulted for a calendar-backed visit — and `status` only
 * without one (a hand-booked visit, or an echo whose facts were not read).
 * Pinned by `services-occurrence-client-parity.test.ts`.
 */
export function isScheduledWork(s) {
    if (s.deliveredBucket)
        return s.deliveredBucket !== 'cancelled' && s.deliveredBucket !== 'vendor_absent';
    if (s.status === 'cancelled')
        return false;
    return !s.vendorAbsentSince && s.occurrenceState !== 'cancelled';
}
/**
 * Is this event a delivered service IN ITS OWN RIGHT — the canonical
 * `DELIVERED_SERVICE_EVENT` (attended, minutes recorded) for a visit with no
 * calendar-backed occurrence? An encounter from an installation whose calendar
 * `calendarIntegrations` carries is counted through its occurrence's
 * `deliveredBucket` instead (or, with no occurrence, on its own
 * `documented_no_occurrence` line, credited by CREATOR_PROVIDER_RULING) —
 * never twice.
 */
export function isOwnDelivery(e, calendarIntegrations) {
    return (e.attendanceStatus === 'attended' &&
        (e.minutes ?? 0) > 0 &&
        !(e.integrationId != null && calendarIntegrations.has(e.integrationId)));
}
//# sourceMappingURL=services.js.map